import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import EquipmentAttributeForm from "../../components/ui/equipmentAttributeForm/equipmentAttributeForm.component";
import { useCRUD } from "../../api/crud";
import { EQUIPMENT_ATTRIBUTES_ENDPOINT, ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const ConfigurationDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, remove } = useCRUD(EQUIPMENT_ATTRIBUTES_ENDPOINT);
  const [editOpened, setEditOpened] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);
  const canChange = useHasPermission("settlement.change_equipmentattribute");
  const canDelete = useHasPermission("settlement.delete_equipmentattribute");

  const handleDelete = async () => {
    if (!id || loading) return;
    const result = await remove({ id });
    if (result?.success) {
      enqueueSnackbar(t("Configuration deleted successfully"), SUCCESS_SNACKBAR);
      setDeleteOpened(false);
      navigate("/equipment-section/configurations");
    } else {
      const msg =
        (typeof result?.errorData === "string"
          ? result.errorData
          : (result?.errorData as { message?: string; detail?: string })?.detail ||
            (result?.errorData as { message?: string; detail?: string })?.message) ||
        t("Failed to delete configuration");
      enqueueSnackbar(msg, ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Edit configuration"),
      onClick: () => setEditOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete configuration"),
      onClick: () => setDeleteOpened(true),
      color: "var(--errorColor)",
      disabled: !canDelete,
    },
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editOpened}
        setOpened={setEditOpened}
        title={t("Edit configuration")}
        content={
          id ? (
            <EquipmentAttributeForm
              id={id}
              onSuccess={() => {
                setEditOpened(false);
                window.dispatchEvent(new Event("equipmentAttributeUpdated"));
              }}
            />
          ) : (
            <></>
          )
        }
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t("Delete configuration")}
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t(
                "Deleting this configuration will permanently remove it from the system. This action cannot be undone."
              )}
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                {t("This action cannot be undone. All associated data will be permanently deleted.")}
              </Typography>
            </Alert>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
              {loading ? t("Processing...") : t("Confirm deletion")}
            </Button>
          </Box>
        }
      />
    </>
  );
};
